import { skipToken } from '@apollo/client/react'
import type { ReactNode } from 'react'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import Text from 'src/components/ui/Text'
import type {
  GenerationModelType,
  GenerationModelVersion,
} from 'src/services/apollo/generated/graphql'
import { useGetGenerationModel } from 'src/services/apollo/generation-model'
import { isLora, isMMDIT26AModel, isUserLora } from 'src/utils/model'

const loraParamsFields = ['triggerWords']
const sdv1ModelParamsFields = [
  'samplingSteps',
  'samplingMethod',
  'cfgScale',
  'negativePrompts',
]

function versionHasParams(
  version?: GenerationModelVersion,
  modelType?: GenerationModelType,
): boolean {
  if (!version || !modelType) return false
  const entries = Object.entries(version.extra ?? {})

  if (isLora(modelType)) {
    return entries.some(
      ([key, value]) => loraParamsFields.includes(key) && value,
    )
  } else {
    return entries.some(
      ([key, value]) => sdv1ModelParamsFields.includes(key) && value,
    )
  }
}

export default function ModelParamsInfo(props: {
  version?: GenerationModelVersion
  modelType: GenerationModelType
  contentClassName?: string
  /**
   * Only available if this model is a trained lora
   * Shows which model is it trained on
   */
  trainBaseModelId?: string
}) {
  const { t } = useTranslation(['market', 'generation', 'action'])
  const { version, modelType, trainBaseModelId } = props

  const hasParams = versionHasParams(version, modelType)

  if (!hasParams) return null

  const content = () => {
    if (!version) return null

    if (isLora(modelType)) {
      return (
        <>
          <ModelParameterItem
            title={t('market:model.trigger-words')}
            value={version.extra.triggerWords}
          />
          {isUserLora(modelType) && trainBaseModelId ? (
            <Suspense>
              <ModelBaseModelParameterItem
                trainBaseModelId={trainBaseModelId}
              />
            </Suspense>
          ) : null}
        </>
      )
    }
    const { samplingSteps, samplingMethod, negativePrompts, cfgScale } =
      version.extra
    const hideMMDIT26AParams = isMMDIT26AModel(modelType)
    return (
      <>
        {!hideMMDIT26AParams && (
          <ModelParameterItem
            title={t('generation:sampling-steps.label')}
            value={samplingSteps}
          />
        )}
        <ModelParameterItem
          title={t('generation:sampling-method.label')}
          value={samplingMethod}
        />
        <ModelParameterItem
          title={t('generation:cfg-scale.label')}
          value={cfgScale}
        />
        {!hideMMDIT26AParams && (
          <ModelParameterItem
            title={t('generation:negative-prompts.label')}
            value={negativePrompts}
            className="col-span-3"
          />
        )}
      </>
    )
  }
  return content()
}

function ModelBaseModelParameterItem(props: { trainBaseModelId?: string }) {
  const { data: trainBaseModel } = useGetGenerationModel(
    props.trainBaseModelId || skipToken,
  )
  return (
    <ModelParameterItem
      title={<Text i18nKey="market:model.trained-based-on-model" />}
      value={trainBaseModel?.title}
    />
  )
}

function ModelParameterItem(
  props: { title: ReactNode; value?: string } & StyleProps,
) {
  const { title, value, className } = props
  if (value) {
    return (
      <div className={className}>
        <dt>{title}</dt>
        <dd>
          <p className="break-words min-w-0">{value}</p>
        </dd>
      </div>
    )
  }
  return null
}
